/**
 * CausalInsightsPanel - Deep Causal Analysis with Progressive Disclosure
 * ============================================================================
 * DC-916: Display causal chains in recommendations
 *
 * This component provides on-demand deep causal analysis using the DC-915 API.
 * It shows root causes, keystone interventions, and counterfactual scenarios
 * to help users understand the "why" behind data quality issues.
 *
 * Features:
 * - Progressive disclosure: Users click to trigger analysis
 * - Root causes with evidence
 * - Prioritized keystone interventions
 * - Counterfactual scenario comparison (best/partial/no_action/worst)
 * - Confidence indicators
 *
 * @version 1.0.0
 * @created 2026-01-11
 * ============================================================================
 */

import React, { useState, useCallback } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import type {
  CausalInsights,
  RootCause,
  KeystoneIntervention,
  CounterfactualScenario,
} from '../types/bridgeIntelligence';
import '../styles/causal-insights-panel.css';

// ============================================================================
// TYPES
// ============================================================================

export interface CausalInsightsPanelProps {
  /** Journey ID to analyze */
  journeyId: string;
  /** Callback when analysis completes */
  onAnalysisComplete?: (insights: CausalInsights) => void;
  /** Optional custom className */
  className?: string;
}

type AnalysisState = 'initial' | 'loading' | 'loaded' | 'error';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getConfidenceColor = (confidence: string): string => {
  switch (confidence) {
    case 'high':
      return 'var(--tri-good)';
    case 'medium':
      return 'var(--tri-soft)';
    case 'low':
      return 'var(--tri-hard)';
    default:
      return 'var(--tri-slate)';
  }
};

const getEffortImpactBadge = (level: string): { color: string; bg: string } => {
  switch (level) {
    case 'high':
      return { color: 'var(--tri-hard)', bg: 'var(--tri-hard)' };
    case 'medium':
      return { color: 'var(--tri-soft)', bg: 'var(--tri-soft)' };
    case 'low':
      return { color: 'var(--tri-good)', bg: 'var(--tri-good)' };
    default:
      return { color: 'var(--tri-slate)', bg: 'var(--tri-ink)' };
  }
};

const getScenarioConfig = (scenario: string): { label: string; color: string; icon: string } => {
  switch (scenario) {
    case 'best_case':
      return { label: 'Best Case', color: 'var(--tri-good)', icon: '🎯' };
    case 'partial_fix':
      return { label: 'Partial Fix', color: 'var(--tri-soft)', icon: '⚡' };
    case 'no_action':
      return { label: 'No Action', color: 'var(--tri-terra)', icon: '⏸️' };
    case 'worst_case':
      return { label: 'Worst Case', color: 'var(--tri-hard)', icon: '⚠️' };
    default:
      return { label: scenario, color: 'var(--tri-slate)', icon: '📊' };
  }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Initial state - prompt user to run analysis
 */
interface InitialStateProps {
  onAnalyze: () => void;
}

const InitialState: React.FC<InitialStateProps> = ({ onAnalyze }) => (
  <div className="causal-initial-state">
    <div className="initial-icon">🔬</div>
    <h3 className="initial-title">Causal Analysis</h3>
    <p className="initial-description">
      Understand the root causes behind your data quality issues and identify
      high-impact interventions that cascade improvements across multiple metrics.
    </p>
    <ul className="initial-features">
      <li>🎯 Root cause identification with evidence</li>
      <li>💡 Prioritized keystone interventions</li>
      <li>📊 Counterfactual scenario projections</li>
    </ul>
    <button className="btn-analyze" onClick={onAnalyze}>
      Analyze Root Causes
    </button>
    <p className="initial-note">Analysis takes 60-90 seconds</p>
  </div>
);

/**
 * Loading state with progress indication
 */
interface LoadingStateProps {
  elapsedSeconds: number;
}

const LoadingState: React.FC<LoadingStateProps> = ({ elapsedSeconds }) => {
  const progress = Math.min((elapsedSeconds / 90) * 100, 95);

  const getStatusMessage = () => {
    if (elapsedSeconds < 15) return 'Gathering station findings...';
    if (elapsedSeconds < 30) return 'Analyzing causal relationships...';
    if (elapsedSeconds < 50) return 'Identifying root causes...';
    if (elapsedSeconds < 70) return 'Generating interventions...';
    return 'Building counterfactual scenarios...';
  };

  return (
    <div className="causal-loading-state">
      <div className="loading-spinner" />
      <h3 className="loading-title">Analyzing Causal Relationships</h3>
      <div className="loading-progress-bar">
        <div
          className="loading-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="loading-status">{getStatusMessage()}</p>
      <p className="loading-time">
        {elapsedSeconds}s elapsed • Estimated: 60-90 seconds
      </p>
    </div>
  );
};

/**
 * Error state with retry option
 */
interface ErrorStateProps {
  error: string;
  onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => (
  <div className="causal-error-state">
    <div className="error-icon">⚠️</div>
    <h3 className="error-title">Analysis Failed</h3>
    <p className="error-message">{error}</p>
    <button className="btn-retry" onClick={onRetry}>
      Try Again
    </button>
  </div>
);

/**
 * Root Causes Section
 */
interface RootCausesSectionProps {
  rootCauses: RootCause[];
  expanded: boolean;
  onToggle: () => void;
}

const RootCausesSection: React.FC<RootCausesSectionProps> = ({
  rootCauses,
  expanded,
  onToggle,
}) => (
  <div className="causal-section causal-section--root-causes">
    <button className="section-header" onClick={onToggle}>
      <div className="section-header-left">
        <span className="section-icon">🔴</span>
        <h4 className="section-title">Root Causes</h4>
        <span className="section-count">{rootCauses.length}</span>
      </div>
      <span className="section-toggle">{expanded ? '▼' : '▶'}</span>
    </button>

    {expanded && (
      <div className="section-content">
        {rootCauses.map((cause) => (
          <div key={cause.id} className="root-cause-card">
            <div className="cause-header">
              <span
                className="confidence-badge"
                style={{
                  color: getConfidenceColor(cause.confidence),
                  borderColor: getConfidenceColor(cause.confidence),
                }}
              >
                {cause.confidence.toUpperCase()}
              </span>
              <p className="cause-description">{cause.description}</p>
            </div>

            <div className="cause-details">
              <div className="cause-evidence">
                <span className="detail-label">Evidence:</span>
                <ul className="evidence-list">
                  {cause.evidence.map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              </div>

              <div className="cause-metrics">
                <span className="detail-label">Affected Metrics:</span>
                <div className="metrics-tags">
                  {cause.affected_metrics.map((metric, idx) => (
                    <span key={idx} className="metric-tag">{metric}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/**
 * Keystone Interventions Section
 */
interface KeystoneInterventionsSectionProps {
  interventions: KeystoneIntervention[];
  expanded: boolean;
  onToggle: () => void;
}

const KeystoneInterventionsSection: React.FC<KeystoneInterventionsSectionProps> = ({
  interventions,
  expanded,
  onToggle,
}) => {
  // Sort by priority score descending
  const sortedInterventions = [...interventions].sort(
    (a, b) => b.priority_score - a.priority_score
  );

  return (
    <div className="causal-section causal-section--interventions">
      <button className="section-header" onClick={onToggle}>
        <div className="section-header-left">
          <span className="section-icon">💡</span>
          <h4 className="section-title">Keystone Interventions</h4>
          <span className="section-count">{interventions.length}</span>
        </div>
        <span className="section-toggle">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="section-content">
          {sortedInterventions.map((intervention, index) => {
            const effortStyle = getEffortImpactBadge(intervention.effort);
            const impactStyle = getEffortImpactBadge(intervention.impact);

            return (
              <div key={intervention.id} className="intervention-card">
                <div className="intervention-header">
                  <span className="intervention-rank">#{index + 1}</span>
                  <span className="priority-score">
                    Score: {intervention.priority_score}
                  </span>
                </div>

                <h5 className="intervention-title">{intervention.title}</h5>
                <p className="intervention-description">{intervention.description}</p>

                <div className="intervention-badges">
                  <span
                    className="badge badge--effort"
                    style={{ color: effortStyle.color, backgroundColor: effortStyle.bg }}
                  >
                    Effort: {intervention.effort.toUpperCase()}
                  </span>
                  <span
                    className="badge badge--impact"
                    style={{ color: impactStyle.color, backgroundColor: impactStyle.bg }}
                  >
                    Impact: {intervention.impact.toUpperCase()}
                  </span>
                </div>

                <div className="cascading-benefits">
                  <span className="benefits-label">Cascading Benefits:</span>
                  <ul className="benefits-list">
                    {intervention.cascading_benefits.slice(0, 3).map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    ))}
                    {intervention.cascading_benefits.length > 3 && (
                      <li className="more-benefits">
                        +{intervention.cascading_benefits.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Counterfactual Scenarios Section
 */
interface CounterfactualScenariosSectionProps {
  scenarios: CounterfactualScenario[];
  expanded: boolean;
  onToggle: () => void;
}

const CounterfactualScenariosSection: React.FC<CounterfactualScenariosSectionProps> = ({
  scenarios,
  expanded,
  onToggle,
}) => {
  // Order scenarios logically
  const scenarioOrder = ['best_case', 'partial_fix', 'no_action', 'worst_case'];
  const orderedScenarios = scenarioOrder
    .map(s => scenarios.find(sc => sc.scenario === s))
    .filter((s): s is CounterfactualScenario => s !== undefined);

  return (
    <div className="causal-section causal-section--scenarios">
      <button className="section-header" onClick={onToggle}>
        <div className="section-header-left">
          <span className="section-icon">📊</span>
          <h4 className="section-title">Counterfactual Scenarios</h4>
          <span className="section-count">{scenarios.length}</span>
        </div>
        <span className="section-toggle">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="section-content">
          <p className="scenarios-intro">
            Compare projected outcomes based on different intervention strategies:
          </p>

          <div className="scenarios-grid">
            {orderedScenarios.map((scenario) => {
              const config = getScenarioConfig(scenario.scenario);

              return (
                <div
                  key={scenario.scenario}
                  className={`scenario-card scenario-card--${scenario.scenario}`}
                >
                  <div
                    className="scenario-header"
                    style={{ borderBottomColor: config.color }}
                  >
                    <span className="scenario-icon">{config.icon}</span>
                    <span
                      className="scenario-label"
                      style={{ color: config.color }}
                    >
                      {config.label}
                    </span>
                  </div>

                  <p className="scenario-description">{scenario.description}</p>

                  <div className="scenario-outcomes">
                    {scenario.projected_outcomes.slice(0, 4).map((outcome, idx) => (
                      <div key={idx} className="outcome-row">
                        <span className="outcome-metric">{outcome.metric}</span>
                        <div className="outcome-values">
                          <span className="outcome-current">{outcome.current}</span>
                          <span className="outcome-arrow">→</span>
                          <span
                            className="outcome-projected"
                            style={{ color: config.color }}
                          >
                            {outcome.projected}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CausalInsightsPanel: React.FC<CausalInsightsPanelProps> = ({
  journeyId,
  onAnalysisComplete,
  className = '',
}) => {
  const { authFetch } = useApiClient();
  const [state, setState] = useState<AnalysisState>('initial');
  const [insights, setInsights] = useState<CausalInsights | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Accordion expansion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    rootCauses: true,
    interventions: true,
    scenarios: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleAnalyze = useCallback(async () => {
    setState('loading');
    setError(null);
    setElapsedSeconds(0);

    // Start elapsed time counter
    const startTime = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const response = await authFetch(
        `/api/bridge/${journeyId}/causal?depth=standard`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      clearInterval(timer);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Analysis failed (${response.status})`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Invalid response from server');
      }

      setInsights(result.data);
      setState('loaded');

      if (onAnalysisComplete) {
        onAnalysisComplete(result.data);
      }
    } catch (err) {
      clearInterval(timer);
      console.error('[CausalInsightsPanel] Analysis error:', err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setState('error');
    }
  }, [journeyId, onAnalysisComplete, authFetch]);

  const handleRefresh = useCallback(() => {
    setInsights(null);
    handleAnalyze();
  }, [handleAnalyze]);

  // Render based on state
  return (
    <div
      className={`causal-insights-panel ${className}`}
      role="region"
      aria-label="Causal Analysis Panel"
    >
      {state === 'initial' && (
        <InitialState onAnalyze={handleAnalyze} />
      )}

      {state === 'loading' && (
        <LoadingState elapsedSeconds={elapsedSeconds} />
      )}

      {state === 'error' && error && (
        <ErrorState error={error} onRetry={handleAnalyze} />
      )}

      {state === 'loaded' && insights && (
        <div className="causal-loaded-state">
          {/* Header with confidence and refresh */}
          <div className="loaded-header">
            <div className="header-left">
              <span className="header-icon">🔬</span>
              <h3 className="header-title">Causal Analysis</h3>
              <span
                className="confidence-indicator"
                style={{ color: getConfidenceColor('high') }}
                title={insights.confidence_notes}
              >
                {insights.analysis_depth === 'deep' ? 'Deep Analysis' : 'Standard Analysis'}
              </span>
            </div>
            <button className="btn-refresh" onClick={handleRefresh} title="Re-run analysis">
              🔄 Refresh
            </button>
          </div>

          {/* Summary stats */}
          <div className="loaded-summary">
            <div className="summary-stat">
              <span className="stat-value">{insights.root_causes.length}</span>
              <span className="stat-label">Root Causes</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{insights.keystone_interventions.length}</span>
              <span className="stat-label">Interventions</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{insights.nodes.length}</span>
              <span className="stat-label">Causal Nodes</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{insights.edges.length}</span>
              <span className="stat-label">Relationships</span>
            </div>
          </div>

          {/* Accordion sections */}
          <div className="loaded-sections">
            <RootCausesSection
              rootCauses={insights.root_causes}
              expanded={expandedSections.rootCauses}
              onToggle={() => toggleSection('rootCauses')}
            />

            <KeystoneInterventionsSection
              interventions={insights.keystone_interventions}
              expanded={expandedSections.interventions}
              onToggle={() => toggleSection('interventions')}
            />

            <CounterfactualScenariosSection
              scenarios={insights.counterfactual_scenarios}
              expanded={expandedSections.scenarios}
              onToggle={() => toggleSection('scenarios')}
            />
          </div>

          {/* Confidence notes footer */}
          <div className="loaded-footer">
            <details className="confidence-notes">
              <summary>Confidence Notes</summary>
              <p>{insights.confidence_notes}</p>
            </details>
            <span className="generated-at">
              Generated: {new Date(insights.generated_at).toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CausalInsightsPanel;
